import type { GetServerSideProps } from 'next';
import { getCtx } from '@shared/server/modules/nextjs/ctxBridge';
import WebsitePage from '@/modules/platform-website/WebsitePage';
import { detectWebsiteDeviceVariant } from '@/modules/platform-website/device';
import { deriveSaleMemberKind } from '@/modules/platform-website/headerContract';
import type { SaleMemberKind, WebsitePageProps, WebsiteViewer } from '@/modules/platform-website/types';
import Mycard from '@/services/mycard';
import styles from './index.module.scss';

export default function Homepage({ deviceVariant, isLoggedIn, saleMemberKind, viewer }: WebsitePageProps) {
  return (
    <div className={styles.pageShell}>
      <WebsitePage
        deviceVariant={deviceVariant}
        isLoggedIn={isLoggedIn}
        saleMemberKind={saleMemberKind}
        viewer={viewer}
      />
    </div>
  );
}

export const getServerSideProps: GetServerSideProps<WebsitePageProps> = async (context) => {
  context.res.setHeader('Cache-Control', 'private, no-store');

  const userAgent = String(context.req.headers['user-agent'] || '');
  const deviceVariant = detectWebsiteDeviceVariant(userAgent);

  // Production mobile is the existing public H5 experience and has no
  // identity-dependent header. Avoid touching or serializing account state.
  if (deviceVariant === 'mobile') {
    return {
      props: {
        deviceVariant,
        isLoggedIn: false,
        saleMemberKind: 'none',
        viewer: null,
      },
    };
  }

  const ctx = await getCtx(context);
  const isLoggedIn = Boolean(ctx.isLoggedIn);
  let saleMemberKind: SaleMemberKind = 'none';
  let viewer: WebsiteViewer | null = null;

  if (isLoggedIn) {
    try {
      const card = await Mycard(ctx);
      const name = typeof card?.name === 'string' ? card.name.trim() : '';
      const avatar = typeof card?.avatar === 'string' ? card.avatar.trim() : '';
      saleMemberKind = deriveSaleMemberKind(card);

      if (name) {
        viewer = avatar ? { name, avatar } : { name };
      }
    } catch {
      ctx.logger?.warn('platform homepage: Mycard unavailable; rendering safe fallback');
    }
  }

  return {
    props: {
      deviceVariant,
      isLoggedIn,
      saleMemberKind,
      viewer,
    },
  };
};
